// Organizasyon teklifleri — sunucu istekleri ve ortak yardımcılar.
// Tüm uçlar token ister (backend/server.js → "2c. ORGANİZASYON TEKLİFLERİ"); yazma uçları 'write',
// silme 'delete' izni ister. Hata durumunda sunucunun Türkçe mesajı Error olarak fırlatılır.
import { API_URL, jsonAuthHeaders, readJsonOrThrow } from '../../utils/api';
import { DEFAULT_VAT_RATE, toEventNumber, computeEventTotals } from '../../utils/eventPricing';

export const DEFAULT_EVENT_SETTINGS = {
  termsText: '',            // boş → eventTerms.js DEFAULT_EVENT_TERMS
  itemLibrary: [],          // [{ id, name, description, quantity, unit, unitPrice, vatRate }]
  packages: [],             // [{ id, name, items: [{ name, description, quantity, unit, unitPrice, vatRate }] }]
  defaultVatMode: 'included',
  defaultVatRate: DEFAULT_VAT_RATE,   // yeni satırın ve oranı olmayan eski kalemin KDV oranı
  defaultValidityDays: 7,
  defaultDepositPercent: 30,
};

const authGet = async (url, token, fallback) =>
  readJsonOrThrow(await fetch(url, { headers: jsonAuthHeaders(token) }), fallback);

const send = async (method, url, token, body, fallback) =>
  readJsonOrThrow(await fetch(url, {
    method, headers: jsonAuthHeaders(token), body: body === undefined ? undefined : JSON.stringify(body),
  }), fallback);

export const fetchEventOffers = (token) => authGet(`${API_URL}/eventOffers`, token, 'Organizasyon teklifleri alınamadı.');
export const fetchEventOffer = (token, id) => authGet(`${API_URL}/eventOffers/${id}`, token, 'Organizasyon teklifi alınamadı.');
export const createEventOffer = (token, payload) => send('POST', `${API_URL}/eventOffers`, token, payload, 'Organizasyon teklifi kaydedilemedi.');
export const updateEventOffer = (token, id, payload) => send('PUT', `${API_URL}/eventOffers/${id}`, token, payload, 'Organizasyon teklifi güncellenemedi.');
export const setEventOfferStatus = (token, id, status) => send('PATCH', `${API_URL}/eventOffers/${id}/status`, token, { status }, 'Durum güncellenemedi.');
export const setEventDepositPaid = (token, id, depositPaid) => send('PATCH', `${API_URL}/eventOffers/${id}/deposit`, token, { depositPaid }, 'Kapora bilgisi güncellenemedi.');
export const deleteEventOffer = (token, id) => send('DELETE', `${API_URL}/eventOffers/${id}`, token, undefined, 'Organizasyon teklifi silinemedi.');

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(String(reader.result).split(',')[1]);
  reader.onerror = () => reject(new Error('PDF okunamadı.'));
  reader.readAsDataURL(blob);
});

// PDF'i sunucuya yükler → { pdfFile, url }. Dosya adı sunucuda üretilir ve teklif boyunca değişmez.
// pdfVersion: PDF'i çizen hesap kuralının sürümü (2 = kalem başına KDV). Sunucu eski sürümün PDF'ini reddeder.
export const EVENT_PDF_VERSION = 2;
export const uploadEventPdf = async (token, id, blob) =>
  send('POST', `${API_URL}/eventOffers/${id}/pdf`, token, { pdfBase64: await blobToBase64(blob), pdfVersion: EVENT_PDF_VERSION }, 'PDF kaydedilemedi.');

// Müşteriye gönderilecek link. PDF hiç yüklenmediyse ya da teklif sonradan düzenlenip PDF yenilenemediyse
// (pdfGeneratedAt NULL) '' → panel paylaşım düğmelerini kapatır, kırık/eski link gitmez.
export const eventPdfLink = (offer) =>
  (offer?.pdfFile && offer?.pdfGeneratedAt ? `${window.location.origin}/offers/view/${offer.pdfFile}` : '');

// Ayarlar (settings kategorisi 'events'). GET token istemez; HİÇ kayıt yoksa sunucu {} döner → varsayılanlar.
// İstek BAŞARISIZ olursa hata fırlatılır: sessizce varsayılana düşseydik PDF'e otelin koşulları yerine örnek
// koşullar basılır, Ayarlar ekranı da kayıtlı paketlerin üzerine boş liste yazabilirdi.
export const fetchEventSettings = async () => {
  let res;
  try {
    res = await fetch(`${API_URL}/settings/events`);
  } catch {
    throw new Error('Organizasyon ayarları alınamadı (bağlantı hatası).');
  }
  if (!res.ok) throw new Error(`Organizasyon ayarları alınamadı (sunucu hatası ${res.status}).`);
  const data = await res.json().catch(() => null);
  return { ...DEFAULT_EVENT_SETTINGS, ...(data && typeof data === 'object' ? data : {}) };
};
export const saveEventSettings = (token, settings) =>
  send('POST', `${API_URL}/settings/events`, token, settings, 'Organizasyon ayarları kaydedilemedi.');

// PDF ve ekranlar için ortak etiketler
export const VAT_MODE_LABELS = { included: 'KDV dahil', excluded: 'KDV hariç' };

let keySeq = 0;
export const newItemKey = () => `item-${Date.now().toString(36)}-${++keySeq}`;

// Form satırı (ham metin alanları) → kayıt/sunucu biçimi (sayılar)
export const itemToPayload = (it) => ({
  name: String(it.name || '').trim(),
  description: String(it.description || '').trim(),
  quantity: toEventNumber(it.quantity),
  unit: String(it.unit || '').trim(),
  unitPrice: toEventNumber(it.unitPrice),
  vatRate: toEventNumber(it.vatRate),
});

// Kayıtlı kalem (sayılar) → form satırı (Türkçe biçimli metin)
const fmtInput = (n) => (Number.isFinite(Number(n)) && n !== '' && n !== null && n !== undefined
  ? Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 2, useGrouping: false }) : '');
// Kalem KDV oranı: kalemde yoksa (eski teklif / eski hazır kalem) fallbackVatRate (teklifin oranı ya da Ayarlar varsayılanı)
const vatInput = (it, fallbackVatRate) => {
  const own = fmtInput(it?.vatRate);
  if (own !== '') return own;
  const fb = fmtInput(fallbackVatRate);
  return fb !== '' ? fb : String(DEFAULT_VAT_RATE);
};
export const itemFromSaved = (it, fallbackVatRate = DEFAULT_VAT_RATE) => ({
  key: newItemKey(),
  name: it?.name || '',
  description: it?.description || '',
  quantity: fmtInput(it?.quantity ?? 1),
  unit: it?.unit || '',
  unitPrice: fmtInput(it?.unitPrice),
  vatRate: vatInput(it, fallbackVatRate),
});

export const emptyItem = (vatRate = DEFAULT_VAT_RATE) => ({
  key: newItemKey(), name: '', description: '', quantity: '1', unit: 'adet', unitPrice: '', vatRate: vatInput(null, vatRate),
});

// En fazla 2 ondalık: sunucu kuruşa yuvarlar; "2,555" kabul edilseydi ekrandaki satır toplamı kayıtla ayrışırdı
export const hasTooManyDecimals = (n) => Number.isFinite(n) && Math.abs(Math.round(n * 100) / 100 - n) > 1e-9;

// Satır doğrulaması (formda kaydetmeden önce) → hata metni ya da ''
export const validateItems = (items) => {
  if (!items || items.length === 0) return 'En az bir kalem ekleyin.';
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    const n = i + 1;
    if (!String(it.name || '').trim()) return `${n}. kalemin adını yazın.`;
    const q = toEventNumber(it.quantity);
    if (!Number.isFinite(q) || q <= 0) return `${n}. kalemin adedi geçersiz ("${it.quantity}"). Ör. 1, 120, 2,5`;
    if (hasTooManyDecimals(q)) return `${n}. kalemin adedi en fazla 2 ondalık olabilir ("${it.quantity}").`;
    const p = toEventNumber(it.unitPrice);
    if (!Number.isFinite(p) || p < 0) return `${n}. kalemin birim fiyatı geçersiz ("${it.unitPrice}"). Ör. 1500 ya da 1.500`;
    if (hasTooManyDecimals(p)) return `${n}. kalemin birim fiyatı en fazla 2 ondalık (kuruş) olabilir ("${it.unitPrice}").`;
    const r = toEventNumber(it.vatRate);
    if (!Number.isFinite(r) || r < 0 || r > 100 || hasTooManyDecimals(r)) return `${n}. kalemin KDV oranını seçin (%0–%100).`;
  }
  return '';
};

export { computeEventTotals };
